import { z } from "@/zod";
import {
  dateTime,
  nonNegativeDecimalStringSchema,
  settlementType,
  ulid,
} from "@/schemas/commonFields";
import { nullableCoordinates } from "@/schemas/coordinates";
import { time } from "./commonFields/time";
import { applyEmptyStringsToNulls } from "@/lib/utils/zod";
import { isEndDateAfterStartDate } from "@/lib/utils/isEndDateAfterStartDate";
import { GeolocationAccuracyLevel } from "@/services/geolocationService";

export const DEVICE_TYPE_MOBILE = "mobile";
export const DEVICE_TYPE_DESKTOP = "desktop";

export const DEVICE_TYPE = [DEVICE_TYPE_MOBILE, DEVICE_TYPE_DESKTOP] as const;

export const VISIT_STATUS_NEW = "new";
export const VISIT_STATUS_STARTED = "started";
export const VISIT_STATUS_COMPLETED = "completed";
export const VISIT_STATUS_CANCELLED = "cancelled";

export const VISIT_STATUS = [
  VISIT_STATUS_NEW,
  VISIT_STATUS_STARTED,
  VISIT_STATUS_COMPLETED,
  VISIT_STATUS_CANCELLED,
] as const;

export type VisitStatus = (typeof VISIT_STATUS)[keyof typeof VISIT_STATUS];

export const contractorVisit = z.object({
  id: ulid,
  project_id: ulid,
  project_name: z.string(),
  client_ref_number: z.string().nullable().optional(),
  client_name: z.string().nullable().optional(),
  client_tax_number: z.string().nullable().optional(),
  store_id: ulid,
  store_name: z.string(),
  store_ref_number: z.number().nullable().optional(),
  client_store_number: z.string().nullable().optional(),
  network: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  street_name: z.string().nullable().optional(),
  street_number: z.string().nullable().optional(),
  postal_code: z.string().nullable().optional(),
  contractor_id: ulid,
  contractor_full_name: z.string().optional(),
  contractor_phone_number: z.string().nullable().optional(),
  contractor_full_name_and_personal_id_number: z.string().optional(),
  date: z.string().date(),
  project_role_id: ulid,
  project_role_name: z.string(),
  start_datetime: dateTime.nullish(),
  end_datetime: dateTime.nullish(),
  suggested_start_time: z.string().nullable(),
  suggested_end_time: z.string().nullable(),
  started_at: dateTime.nullable(),
  ended_at: dateTime.nullable(),
  pr_planned_duration: nonNegativeDecimalStringSchema,
  planned_duration: nonNegativeDecimalStringSchema,
  kam_planned_duration: nonNegativeDecimalStringSchema,
  real_duration: nonNegativeDecimalStringSchema,
  planned_duration_visible: z.boolean().default(false),
  confirmation_user_cognito_id: ulid.nullable(),
  confirmation_user_name: z.string(),
  status: z.enum(VISIT_STATUS),
  device_type: z.enum(DEVICE_TYPE),
  settlement_type: settlementType,
  start_latitude: nullableCoordinates.shape.latitude,
  start_longitude: nullableCoordinates.shape.longitude,
  confirmation_latitude: nullableCoordinates.shape.latitude,
  confirmation_longitude: nullableCoordinates.shape.longitude,
  geolocalization_enabled: z.boolean(),
  editable_by_pr: z.boolean().optional(),
  can_be_confirmed: z.boolean().optional(),
  can_be_cancelled: z.boolean().optional(),
  can_be_deleted: z.boolean().optional(),
  can_be_edited: z.boolean().optional(),
  hours_editable_by_contractor: z.boolean().default(false),
  confirmable_by_contractor: z.boolean().default(false),
  edit_allowed: z.boolean().optional().default(true),
  regional_director_name: z.string().nullable(),
  regional_representative_name: z.string().nullable(),
  supervisor_name: z.string().nullable(),
  store_coordinator_name: z.string().nullable(),
  start_geolocation_accuracy: z.nativeEnum(GeolocationAccuracyLevel).nullable(),
  confirmation_geolocation_accuracy: z
    .nativeEnum(GeolocationAccuracyLevel)
    .nullable(),
  invoicing_company: z.string().nullable(),
  project_status: z.string().nullable(),
  payroll_type: z.string().nullable(),
  project_type: z.string().nullable(),
});

export const summaryContractorVisit = z
  .object({
    id: ulid,
    contractor_id: ulid,
    contractor_full_name_and_personal_id_number: z.string(),
    real_duration_sum: nonNegativeDecimalStringSchema,
    kam_planned_duration_sum: nonNegativeDecimalStringSchema,
    pr_planned_duration_sum: nonNegativeDecimalStringSchema,
    planned_duration_sum: nonNegativeDecimalStringSchema,
    hours_settlement_duration_sum: nonNegativeDecimalStringSchema,
    visits_count: z.number(),
  })
  .readonly();

export const readContractorVisit = contractorVisit.readonly();

export const fullListingContractorVisit = readContractorVisit
  .unwrap()
  .omit({
    can_be_cancelled: true,
    can_be_confirmed: true,
    can_be_deleted: true,
    can_be_edited: true,
  })
  .readonly();

const fullOnlyFields = {
  start_datetime: true,
  end_datetime: true,
  pr_planned_duration: true,
  planned_duration: true,
  kam_planned_duration: true,
  real_duration: true,
  planned_duration_visible: true,
  editable_by_pr: true,
  hours_editable_by_contractor: true,
  confirmable_by_contractor: true,
} as const;

export const basicListingContractorVisit = fullListingContractorVisit
  .unwrap()
  .omit(fullOnlyFields)
  .readonly();

export const fullEditorContractorVisit = readContractorVisit
  .unwrap()
  .omit({
    can_be_cancelled: true,
    can_be_confirmed: true,
  })
  .readonly();

export const basicEditorContractorVisit = fullEditorContractorVisit
  .unwrap()
  .omit(fullOnlyFields)
  .readonly();

export const basicSummaryContractorVisit = summaryContractorVisit
  .unwrap()
  .pick({
    id: true,
    contractor_id: true,
    contractor_full_name_and_personal_id_number: true,
    visits_count: true,
  })
  .readonly();

const updateContractorVisitBase = applyEmptyStringsToNulls({
  id: ulid,
  start_datetime: dateTime.nullable(),
  end_datetime: dateTime.nullable(),
  suggested_start_time: time.nullable(),
  suggested_end_time: time.nullable(),
  pr_planned_duration: nonNegativeDecimalStringSchema.nullable(),
  kam_planned_duration: nonNegativeDecimalStringSchema.nullable(),
});

export const updateContractorVisit = updateContractorVisitBase.superRefine(
  ({ start_datetime, end_datetime }, ctx) => {
    if (!isEndDateAfterStartDate(start_datetime, end_datetime)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "projectContractorVisits.validation.endAfterStart",
        path: ["end_datetime"],
      });
    }
  },
);

export const createContractorVisit = z.object({
  project_role_id: ulid,
  store_id: ulid,
  contractor_id: ulid,
  date: z.string().date(),
  suggested_start_time: z.preprocess(
    (val) => (val === "" || val === null ? undefined : val),
    time.optional(),
  ),
  suggested_end_time: z.preprocess(
    (val) => (val === "" || val === null ? undefined : val),
    time.optional(),
  ),
  pr_planned_duration: nonNegativeDecimalStringSchema,
  settlement_type: settlementType,
});

export const confirmContractorVisitBase = z.object({
  start_datetime: dateTime,
  end_datetime: dateTime,
});

export const confirmContractorVisit = confirmContractorVisitBase.superRefine(
  ({ start_datetime, end_datetime }, ctx) => {
    if (!isEndDateAfterStartDate(start_datetime, end_datetime)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "projectContractorVisits.validation.endAfterStart",
        path: ["end_datetime"],
      });
    }
  },
);

export const cancelContractorVisit = z.object({
  status: z.enum(["cancelled"]),
});
